// frontend/src/components/Project/SprintHistoryDetailModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Flag as FlagIcon,
  Task as TaskIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { alpha, styled } from '@mui/material/styles';
import { sprintsAPI } from '../../services/api';

import {
  TASK_STATUS,
  getTaskStatusText,
  getTaskStatusMuiColor
} from '../../constants/taskConstants';

const ACCENTS = {
  blue: '#2196F3',
  green: '#00A896',
  violet: '#7357E8',
  cyan: '#0EA5B7'
};

const GlassDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: 'min(900px, calc(100% - 24px))',
    maxWidth: 900,
    maxHeight: '90vh',
    borderRadius: theme.spacing(4),
    overflow: 'hidden',
    border: `1px solid ${alpha(theme.palette.common.white, 0.9)}`,
    background: `
      linear-gradient(145deg,
        ${alpha(theme.palette.common.white, 0.70)},
        ${alpha(theme.palette.grey[100], 0.44)} 48%,
        ${alpha(theme.palette.primary.main, 0.10)}
      ),
      radial-gradient(circle at 8% 8%, ${alpha(theme.palette.info.light, 0.18)}, transparent 36%),
      radial-gradient(circle at 92% 92%, ${alpha(theme.palette.primary.main, 0.18)}, transparent 40%)
    `,
    backgroundColor: alpha(theme.palette.common.white, 0.50),
    backdropFilter: 'blur(30px) saturate(145%)',
    WebkitBackdropFilter: 'blur(30px) saturate(145%)',
    boxShadow: `0 30px 100px ${alpha(theme.palette.common.black, 0.22)}`,
    [theme.breakpoints.down('sm')]: {
      width: 'calc(100% - 16px)',
      maxHeight: 'calc(100dvh - 16px)',
      borderRadius: theme.spacing(3)
    }
  },
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.grey[900], 0.30)
  }
}));

const GlassTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  color: theme.palette.text.primary,
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.58)}, ${alpha(theme.palette.common.white, 0.18)})`,
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 2)
  }
}));

const GlassContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4, 3, 3),
  overflowY: 'auto',
  color: theme.palette.text.primary,
  '&::-webkit-scrollbar': {
    width: 8
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.common.white, 0.32),
    borderRadius: 999
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.text.secondary, 0.25),
    borderRadius: 999
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2)
  }
}));

const GlassPanel = styled(Paper)(({ theme, accent = ACCENTS.blue }) => ({
  borderRadius: theme.spacing(3.5),
  border: `1px solid ${alpha(theme.palette.common.white, 0.78)}`,
  background: `
    linear-gradient(145deg,
      ${alpha(theme.palette.common.white, 0.62)},
      ${alpha(theme.palette.common.white, 0.30)}
    ),
    radial-gradient(circle at 100% 0%, ${alpha(accent, 0.15)}, transparent 42%)
  `,
  backgroundColor: alpha(theme.palette.common.white, 0.42),
  backdropFilter: 'blur(18px) saturate(135%)',
  WebkitBackdropFilter: 'blur(18px) saturate(135%)',
  boxShadow: `0 10px 30px ${alpha(accent, 0.07)}`
}));

const StatCard = styled(Paper)(({ theme, accent }) => ({
  minHeight: 88,
  padding: theme.spacing(1.35),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  borderRadius: theme.spacing(3.25),
  border: `1px solid ${alpha(theme.palette.common.white, 0.82)}`,
  background: `
    linear-gradient(145deg,
      ${alpha(theme.palette.common.white, 0.66)},
      ${alpha(accent, 0.13)}
    )
  `,
  backgroundColor: alpha(theme.palette.common.white, 0.42),
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  boxShadow: `0 10px 28px ${alpha(accent, 0.08)}`
}));

const SprintHistoryDetailModal = ({ open, onClose, sprint, isViewOnly = false }) => {
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && sprint?.id && !isViewOnly) {
      fetchSprintTasks();
    } else if (open && sprint?.id && isViewOnly) {
      setTasks([]);
    }
  }, [open, sprint?.id, isViewOnly]);

  const fetchSprintTasks = async () => {
    if (!sprint?.id) return;

    setLoading(true);
    setError(null);

    try {
      const tasksData = await sprintsAPI.getSprintTasks(sprint.id);
      setTasks(Array.isArray(tasksData) ? tasksData : []);
    } catch (error) {
      console.error('Ошибка загрузки задач спринта:', error);
      setError('Не удалось загрузить задачи спринта');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указана';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (e) {
      return 'Не указана';
    }
  };

  const getUserInitials = (user) => {
    if (!user) return '?';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`;
    return initials.trim() || user.email?.charAt(0)?.toUpperCase() || '?';
  };

  if (!sprint) return null;

  const startDate = sprint.start_date ? new Date(sprint.start_date) : null;
  const endDate = sprint.end_date ? new Date(sprint.end_date) : null;

  const duration = startDate && endDate
    ? Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1
    : 0;

  const completedTasks = tasks.filter(t => t.status === TASK_STATUS.DONE).length;
  const totalTasks = tasks.length;
  const completionRate = totalTasks > 0
    ? Math.round((completedTasks / totalTasks) * 100)
    : 0;

  return (
    <GlassDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="sprint-history-dialog-title"
    >
      <GlassTitle id="sprint-history-dialog-title">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 50,
                height: 50,
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '50%',
                color: '#fff',
                background: `linear-gradient(145deg, ${ACCENTS.green}, #5ED6C5)`,
                boxShadow: `0 10px 26px ${alpha(ACCENTS.green, 0.20)}`
              }}
            >
              <CheckCircleIcon />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{
                  color: 'text.primary',
                  lineHeight: 1.15,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {sprint.title || 'Спринт'}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.7, flexWrap: 'wrap' }}>
                <Chip
                  label="Завершённый спринт"
                  size="small"
                  sx={{
                    height: 28,
                    borderRadius: 999,
                    color: ACCENTS.green,
                    fontWeight: 500,
                    backgroundColor: alpha('#5ED6C5', 0.18),
                    border: `1px solid ${alpha('#5ED6C5', 0.40)}`
                  }}
                />
                {isViewOnly && (
                  <Tooltip title="Режим только для просмотра">
                    <LockIcon
                      fontSize="small"
                      sx={{ color: 'text.secondary', opacity: 0.65 }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>

          <IconButton
            onClick={onClose}
            aria-label="Закрыть"
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: '50%',
              color: 'text.primary',
              backgroundColor: alpha('#fff', 0.56),
              border: `1px solid ${alpha('#fff', 0.90)}`,
              boxShadow: `0 8px 24px ${alpha('#000', 0.06)}`,
              '&:hover': {
                backgroundColor: alpha('#fff', 0.82)
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </GlassTitle>

      <GlassContent dividers={false}>
        <Grid container spacing={2.2} sx={{ mb: 2.5, mt: 0.3 }}>
          {/* Цель — крупный блок слева */}
          <Grid item xs={12} md={7}>
            <GlassPanel
              accent={ACCENTS.violet}
              sx={{
                p: 2.7,
                borderRadius: 5.5,
                height: '100%',
                minHeight: 168,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  color: ACCENTS.violet,
                  fontWeight: 600,
                  letterSpacing: 1.0,
                  fontSize: '0.68rem',
                  mb: 1
                }}
              >
                Цель спринта
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'text.primary',
                  lineHeight: 1.7,
                  fontWeight: 350,
                  fontSize: '0.96rem'
                }}
              >
                {sprint.goal || 'Цель не указана'}
              </Typography>
            </GlassPanel>
          </Grid>

          {/* Даты и длительность — компактная вертикальная колонка справа */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2.2
              }}
            >
              <GlassPanel
                accent={ACCENTS.blue}
                sx={{
                  p: 2,
                  borderRadius: 5.5,
                  flex: 1,
                  minHeight: 78,
                  display: 'flex',
                  alignItems: 'center',
                  boxSizing: 'border-box'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.4,
                    width: '100%'
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      borderRadius: '50%',
                      color: ACCENTS.blue,
                      backgroundColor: alpha(ACCENTS.blue, 0.12)
                    }}
                  >
                    <CalendarIcon />
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        fontWeight: 500,
                        fontSize: '0.72rem',
                        mb: 0.2
                      }}
                    >
                      Даты проведения
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 400,
                        fontSize: '0.88rem',
                        lineHeight: 1.45
                      }}
                    >
                      {formatDate(sprint.start_date)} — {formatDate(sprint.end_date)}
                    </Typography>
                  </Box>
                </Box>
              </GlassPanel>

              <GlassPanel
                accent={ACCENTS.violet}
                sx={{
                  p: 2,
                  borderRadius: 5.5,
                  flex: 1,
                  minHeight: 78,
                  display: 'flex',
                  alignItems: 'center',
                  boxSizing: 'border-box'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.4,
                    width: '100%'
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      borderRadius: '50%',
                      color: ACCENTS.violet,
                      backgroundColor: alpha(ACCENTS.violet, 0.12)
                    }}
                  >
                    <FlagIcon />
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        fontWeight: 500,
                        fontSize: '0.72rem',
                        mb: 0.2
                      }}
                    >
                      Длительность
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 400,
                        fontSize: '0.88rem',
                        lineHeight: 1.45
                      }}
                    >
                      {duration} {duration === 1 ? 'день' : duration < 5 ? 'дня' : 'дней'}
                    </Typography>
                  </Box>
                </Box>
              </GlassPanel>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <AssessmentIcon sx={{ color: ACCENTS.violet }} />
          <Typography variant="h6" fontWeight={500} sx={{ color: 'text.primary', letterSpacing: -0.15 }}>
            Итоги спринта
          </Typography>
        </Box>

        {isViewOnly ? (
          <GlassPanel accent={ACCENTS.blue} sx={{ p: 4, textAlign: 'center', mb: 2.5 }}>
            <LockIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5, opacity: 0.5 }} />
            <Typography variant="h6" gutterBottom fontWeight={750} sx={{ color: 'text.primary' }}>
              Детальная статистика недоступна
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Для просмотра задач спринта необходимо стать участником проекта
            </Typography>
          </GlassPanel>
        ) : (
          <>
            <Grid container spacing={1.7} sx={{ mb: 2.7 }}>
              <Grid item xs={12} sm={4}>
                <StatCard accent={ACCENTS.blue}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      borderRadius: '50%',
                      color: ACCENTS.blue,
                      backgroundColor: alpha(ACCENTS.blue, 0.12)
                    }}
                  >
                    <TaskIcon />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={450} sx={{ color: 'text.primary', lineHeight: 1 }}>
                      {totalTasks}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                      Всего задач
                    </Typography>
                  </Box>
                </StatCard>
              </Grid>

              <Grid item xs={12} sm={4}>
                <StatCard accent={ACCENTS.green}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      borderRadius: '50%',
                      color: ACCENTS.green,
                      backgroundColor: alpha(ACCENTS.green, 0.12)
                    }}
                  >
                    <CheckCircleIcon />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={450} sx={{ color: 'text.primary', lineHeight: 1 }}>
                      {completedTasks}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                      Выполнено
                    </Typography>
                  </Box>
                </StatCard>
              </Grid>

              <Grid item xs={12} sm={4}>
                <StatCard accent={ACCENTS.violet}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      borderRadius: '50%',
                      color: ACCENTS.violet,
                      backgroundColor: alpha(ACCENTS.violet, 0.12)
                    }}
                  >
                    <AccessTimeIcon />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={450} sx={{ color: 'text.primary', lineHeight: 1 }}>
                      {completionRate}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                      Завершено
                    </Typography>
                  </Box>
                </StatCard>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <AssignmentIcon sx={{ color: ACCENTS.blue }} />
              <Typography variant="h6" fontWeight={500} sx={{ color: 'text.primary', letterSpacing: -0.15 }}>
                Задачи спринта
              </Typography>
              {totalTasks > 0 && (
                <Chip
                  size="small"
                  label={`${completedTasks} из ${totalTasks}`}
                  sx={{
                    ml: 'auto',
                    height: 24,
                    borderRadius: 999,
                    color: ACCENTS.green,
                    fontWeight: 500,
                    fontSize: '0.68rem',
                    backgroundColor: alpha(ACCENTS.green, 0.10),
                    border: `1px solid ${alpha(ACCENTS.green, 0.22)}`
                  }}
                />
              )}
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert
                severity="error"
                sx={{
                  mt: 2,
                  borderRadius: 2.5,
                  backdropFilter: 'blur(16px)',
                  backgroundColor: alpha('#fff', 0.68)
                }}
              >
                {error}
              </Alert>
            ) : tasks.length > 0 ? (
              <GlassPanel accent={ACCENTS.blue} sx={{ p: 0, overflow: 'hidden' }}>
                <List disablePadding>
                  {tasks.map((task, index) => {
                    const statusColor = getTaskStatusMuiColor(task.status);

                    return (
                      <React.Fragment key={task.id}>
                        <ListItem
                          sx={{
                            mx: 1,
                            my: 0.7,
                            px: 1.6,
                            py: 1.25,
                            alignItems: 'flex-start',
                            borderRadius: 3,
                            border: `1px solid ${alpha('#fff', 0.68)}`,
                            background: `linear-gradient(145deg, ${alpha('#fff', 0.46)}, ${alpha('#fff', 0.20)})`,
                            backdropFilter: 'blur(14px)',
                            WebkitBackdropFilter: 'blur(14px)',
                            boxShadow: `0 6px 20px ${alpha('#000', 0.04)}`,
                            transition: 'transform 150ms ease, background-color 150ms ease, box-shadow 150ms ease',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              backgroundColor: alpha('#fff', 0.34),
                              boxShadow: `0 9px 24px ${alpha('#000', 0.07)}`
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 48, mt: 0.15 }}>
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                color: '#fff',
                                backgroundColor:
                                  statusColor === 'success'
                                    ? ACCENTS.green
                                    : statusColor === 'info'
                                      ? ACCENTS.blue
                                      : statusColor === 'warning'
                                        ? '#E6A23C'
                                        : '#8A94A6',
                                boxShadow: `0 6px 16px ${alpha('#000', 0.10)}`
                              }}
                            >
                              {task.status === TASK_STATUS.DONE ? (
                                <CheckCircleIcon fontSize="small" />
                              ) : task.status === TASK_STATUS.IN_PROGRESS ? (
                                <AccessTimeIcon fontSize="small" />
                              ) : (
                                <TaskIcon fontSize="small" />
                              )}
                            </Avatar>
                          </ListItemIcon>

                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  flexWrap: 'wrap'
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={450}
                                  sx={{ color: 'text.primary', fontSize: '0.88rem' }}
                                >
                                  {task.title}
                                </Typography>

                                <Chip
                                  label={getTaskStatusText(task.status)}
                                  size="small"
                                  color={statusColor}
                                  sx={{
                                    height: 24,
                                    borderRadius: 999,
                                    fontSize: '0.70rem',
                                    fontWeight: 700
                                  }}
                                />
                              </Box>
                            }
                            secondary={
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  mt: 0.8,
                                  flexWrap: 'wrap'
                                }}
                              >
                                {task.assignee && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                    <PersonIcon sx={{ fontSize: 17, color: ACCENTS.blue }} />
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 350, fontSize: '0.70rem' }}>
                                      {task.assignee.first_name || task.assignee.email}
                                    </Typography>
                                  </Box>
                                )}

                                {task.due_date && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                    <CalendarIcon sx={{ fontSize: 17, color: ACCENTS.violet }} />
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 350, fontSize: '0.70rem' }}>
                                      {formatDate(task.due_date)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              </GlassPanel>
            ) : (
              <GlassPanel accent={ACCENTS.blue} sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  В этом спринте не было задач
                </Typography>
              </GlassPanel>
            )}
          </>
        )}
      </GlassContent>

      <DialogActions
        sx={(theme) => ({
          px: 3,
          py: 2,
          borderTop: `1px solid ${alpha('#fff', 0.62)}`,
          background: `linear-gradient(135deg, ${alpha('#fff', 0.42)}, ${alpha('#fff', 0.14)})`,
          [theme.breakpoints.down('sm')]: {
            px: 2
          }
        })}
      >
        <Button
          onClick={onClose}
          variant="text"
          sx={{
            minHeight: 42,
            px: 2.5,
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 750,
            color: 'text.primary',
            backgroundColor: alpha('#fff', 0.56),
            border: `1px solid ${alpha('#fff', 0.88)}`,
            boxShadow: `0 8px 24px ${alpha('#000', 0.06)}`,
            '&:hover': {
              backgroundColor: alpha('#fff', 0.82)
            }
          }}
        >
          Закрыть
        </Button>
      </DialogActions>
    </GlassDialog>
  );
};

export default SprintHistoryDetailModal;
